// ===================================================
// src/context/AuthContext.js
// Supports normal login + OTP verification
// Keeps user and token state synchronized without reload
// ===================================================

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api'; // make sure api.js has login(), verifyOtp(), getMe()

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [loading, setLoading] = useState(true);

  // ===================================================
  // Load user on app start (if token exists)
  // ===================================================
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.getMe();
        if (response.data?.user) {
          setUser(response.data.user);
          setIsAuthenticated(true);
        } else {
          logout();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, [token]);

  // ===================================================
  // Login (Handles both normal + OTP flow)
  // ===================================================
  const login = async (username, password, otp = null, tempToken = null) => {
    try {
      // STEP 1: Initial login (credentials only)
      if (!otp) {
        const response = await authAPI.login({ username, password });

        // If server asks for OTP
        if (response.data.tempToken) {
          return {
            success: false,
            requiresOtp: true,
            tempToken: response.data.tempToken,
          };
        }

        // If classic login (without OTP)
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        setToken(token);
        setUser(user);
        setIsAuthenticated(true);
        return { success: true };
      }

      // STEP 2: Verify OTP
      const response = await authAPI.verifyOtp({ otp, tempToken });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Login/OTP failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed',
      };
    }
  };

  // ===================================================
  // Logout
  // ===================================================
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // ===================================================
  // Helpers
  // ===================================================
  const isAdmin = () => user?.role === 'admin';

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    logout,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
